import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Modal, Form, Button } from "semantic-ui-react";
import { CoreLocationWithAllIncludes } from "../../../../../../queries/locations";
import ControlledLocation from "../../../../../../components/global_components/form_components/ControlledLocation";
import useCoreLocation from "../../../inspections/InspectionPanel/useCoreLocation";
import ControlledAutofilledAddress from "../../../../../../components/global_components/form_components/ControlledAutofilledAddress";
import StaffLocationTabChangeLocationModalHeader from "./StaffLocationTabChangeLocationModal/StaffLocationTabChangeLocationModalHeader";
import StaffLocationTabChangeLocationModalLocationLabel from "./StaffLocationTabChangeLocationModal/StaffLocationTabChangeLocationModalLocationLabel";

interface StaffLocationTabChangeLocationModalProps {
  open: boolean;
  isLoading: boolean;
  defaultLocationId?: number | null;
  defaultAddress?: {
    attn?: string | null;
    address: string;
    address2?: string | null;
    city: string;
    state: string;
    zip: string;
    countryId: string;
  } | null;
  allowEnterManually?: boolean;
  onClose: () => void;
  onValidSubmit: ValidSubmitHandler;
}

export type ValidSubmitHandler = (data: FormData) => void;

interface FormData {
  location: CoreLocationWithAllIncludes | null;
  address: {
    attn?: string | null;
    address: string;
    address2?: string | null;
    city: string;
    state: string;
    zip: string;
    countryId: string;
  } | null;
}

export default function StaffLocationTabChangeLocationModal({
  open,
  isLoading,
  defaultLocationId = null,
  defaultAddress = null,
  allowEnterManually = false,
  onClose,
  onValidSubmit,
}: StaffLocationTabChangeLocationModalProps) {
  const { control, handleSubmit, errors, reset } = useForm<FormData>({
    defaultValues: {
      location: null,
      address: null,
    },
  });

  const [enteringManually, setEnteringManually] = useState<boolean>(false);

  // Use a default location to prefill the modals form
  const { data } = useCoreLocation({ id: defaultLocationId });
  const location = (data as any) ?? null;
  useEffect(() => {
    // If there is a defaultLocationId, fill in the modal using the location
    if (data) {
      reset({
        location,
        address: data?.locationAddress ?? null,
      });
      // If there is a defaultAddress provided WITHOUT a default location, use the address
    } else {
      reset({
        location: null,
        address: defaultAddress,
      });
    }
  }, [data, defaultAddress]);

  const handleValidSubmit: SubmitHandler<FormData> = (data) => {
    onValidSubmit(data);
  };
  const handleEnterManually = () => {
    setEnteringManually(true);
  };
  const handleReturnToLocationSearch = () => {
    setEnteringManually(false);
  };

  return (
    <Modal size='tiny' open={open} onClose={onClose}>
      <Modal.Header>
        <StaffLocationTabChangeLocationModalHeader
          allowEnterManually={allowEnterManually}
          enteringManually={enteringManually}
          onReturnToLocationSearch={handleReturnToLocationSearch}
        />
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field className='tw-bor' error={!!errors.location} required>
            <StaffLocationTabChangeLocationModalLocationLabel
              allowEnterManually={allowEnterManually}
              enteringManually={enteringManually}
              onEnterManually={handleEnterManually}
              onReturnToLocationSearch={handleReturnToLocationSearch}
            />
            {enteringManually ? (
              <ControlledAutofilledAddress
                control={control}
                name='address'
                showCountryField={true}
                autofillCountry={true}
                required
              />
            ) : (
              <ControlledLocation
                control={control}
                name='location'
                required
                defaultValue={location}
                showEmptyFields={false}
              />
            )}
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button secondary onClick={onClose}>
          Cancel
        </Button>
        <Button primary onClick={handleSubmit(handleValidSubmit)} loading={isLoading} disabled={isLoading}>
          Change
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
